import { useState, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { CollapsablePanel } from '@app/app/_components/CollapsablePanel/CollapsablePanel';
import { ReadOnlyField } from '@app/app/_components/ReadOnlyField/ReadOnlyField';
import { TextButton } from '@app/app/_components/TextButton/TextButton';
import { UpdateModal } from '@app/app/esales/transport/components/UpdateModal';
import { formatAddress } from '@app/lib/address-formatter/formatAddress';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseHomeInsuranceResult } from '@app/service/eshop/home/actions/purchaseHomeInsurance';
import { HomeInsuranceRequestResult } from '@app/service/eshop/home/actions/requestHomeInsurance';
import { ProductDataObject } from '@app/domain/model/eshop/home/ProductDataObject';
import { ProductDataPurpose } from '@app/domain/model/eshop/transport/ProductDataPurpose';
import { PolicyholderFormFields } from '@app/app/esales/home/_sections/HomeInsuranceWizardSection/hooks/usePolicyholderFormFields';
import { Input } from '@app/app/_components/Input/Input';
import { findLithuanianRegistryObjectNoHref } from '@app/infrastructure/eshop/findLithuanianRegistryObjectNoHref';

interface Props {
    requestResult: Nullable<HomeInsuranceRequestResult>;
    policyholderFields: PolicyholderFormFields;
    formState: FormState<PurchaseHomeInsuranceResult>;
    objects: ProductDataObject[];
    purposes: Nullable<ProductDataPurpose>;
    onEdit: () => void;
}

export function PropertyInformationPanel({
    requestResult,
    policyholderFields,
    formState,
    objects,
    purposes,
    onEdit,
}: Props) {
    const t = useTranslations();
    const [updateModalOpen, setUpdateModalOpen] = useState(false);

    const panelLabel = formatAddress(requestResult?.address ?? null) || t('eshop.home.objectSummary.property');

    const ownerName = useMemo(() => {
        if (requestResult?.propertyInfo?.policyholder) {
            return `${requestResult.propertyInfo.policyholder.name} ${requestResult.propertyInfo.policyholder.lastName}`;
        }
        return `${policyholderFields.firstName} ${policyholderFields.lastName}`;
    }, [requestResult?.propertyInfo, policyholderFields.firstName, policyholderFields.lastName]);

    const objectTypeName = useMemo(() => {
        const code = formState.data?.formData.objectType || requestResult?.propertyInfo?.objectType;
        return objects.find(o => o.code === code)?.name ?? code ?? '';
    }, [formState.data?.formData.objectType, requestResult?.propertyInfo?.objectType, objects]);

    const constructionTypeName = useMemo(() => {
        const code = formState.data?.formData.constructionType || requestResult?.propertyInfo?.typeOfConstruction;
        return purposes?.purposes.find(p => p.code === code)?.name ?? code ?? '';
    }, [formState.data?.formData.constructionType, requestResult?.propertyInfo?.typeOfConstruction, purposes]);

    const totalArea = useMemo(() => {
        const value = formState.data?.formData.totalArea || requestResult?.propertyInfo?.enclosedArea;
        return value != null ? String(value) : '';
    }, [formState.data?.formData.totalArea, requestResult?.propertyInfo?.enclosedArea]);

    const constructionYear = useMemo(() => {
        const value = formState.data?.formData.constructionYear || requestResult?.propertyInfo?.constructionYear;
        return value != null ? String(value) : '';
    }, [formState.data?.formData.constructionYear, requestResult?.propertyInfo?.constructionYear]);

    const handleOpenUpdateModal = () => {
        setUpdateModalOpen(true);
    };

    const handleCloseUpdateModal = () => {
        setUpdateModalOpen(false);
    };

    const handleProceed = () => {
        handleCloseUpdateModal();
        onEdit();
    };

    return (
        <>
            <CollapsablePanel
                label={panelLabel}
                collapsable={false}
                addon={
                    <TextButton preventDefault onClick={handleOpenUpdateModal}>
                        {t('general.edit')}
                    </TextButton>
                }>
                <div className="px-6 py-2">
                    {requestResult?.propertyInfo == null || requestResult?.propertyInfo?.uniqueObjectNumber == null ? (
                        <Input
                            name="uniqueNumber"
                            noSpaces
                            className="mb-4 w-full"
                            error={formState.violations?.uniqueNumber}
                            label={t('eshop.home.summary.uniqueNumberLabel')}
                            description={
                                <TextButton
                                    href={findLithuanianRegistryObjectNoHref(requestResult?.address ?? null)}
                                    target="_blank">
                                    {t('eshop.home.summary.findObjectNo')}
                                </TextButton>
                            }
                            placeholder={t('eshop.home.summary.uniqueNumberPlaceholder')}
                        />
                    ) : (
                        <ReadOnlyField className="mb-2" label={t('eshop.home.summary.uniqueNumberLabel')}>
                            {requestResult.propertyInfo.uniqueObjectNumber}
                        </ReadOnlyField>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                        <ReadOnlyField label={t('eshop.home.summary.ownerLabel')}>{ownerName}</ReadOnlyField>
                        <ReadOnlyField label={t('eshop.home.summary.personalCodeLabel')}>
                            {requestResult?.policyHolderCode ?? ''}
                        </ReadOnlyField>
                        <ReadOnlyField label={t('eshop.home.summary.objectTypeLabel')}>{objectTypeName}</ReadOnlyField>
                        <ReadOnlyField label={t('eshop.home.summary.constructionTypeLabel')}>
                            {constructionTypeName}
                        </ReadOnlyField>
                        <ReadOnlyField label={t('eshop.home.summary.totalAreaLabel')}>{totalArea}</ReadOnlyField>
                        <ReadOnlyField label={t('eshop.home.summary.constructionYearLabel')}>
                            {constructionYear}
                        </ReadOnlyField>
                    </div>
                </div>
            </CollapsablePanel>

            <UpdateModal
                updateModalOpen={updateModalOpen}
                handleCloseUpdateModal={handleCloseUpdateModal}
                navigateToStep={handleProceed}
            />
        </>
    );
}
